import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "theme-ui";
import { useProfileUpdate } from "./useProfileUpdate";
import { Layout, Spinner } from "@dsaui/components";
import { PageShell } from "components/PageShell";
import { PageTitle } from "components/PageTitle";
import { AccountForm, ProfileMultiForm } from "components/ProfileForm";
import Skeleton from "components/ProfileForm/AccountFormSkeleton";
import MemberDetails from "components/MemberDetails";

interface ProfilePageProps {
  variant: "profile" | "account";
}

const ProfilePage = ({ variant }: ProfilePageProps): JSX.Element | null => {
  const { t } = useTranslation();
  const { fullProfileFormValues, handleProfileUpdate, profile, profileReady, ready } =
    useProfileUpdate();

  if (variant === "account") {
    return (
      <PageShell className="max-w-3xl">
        <PageTitle>{t("pages.account")}</PageTitle>
        {profileReady && fullProfileFormValues ? (
          <AccountForm
            handleProfileUpdate={handleProfileUpdate}
            profile={fullProfileFormValues}
            ready={ready}
          />
        ) : (
          <Skeleton />
        )}
      </PageShell>
    );
  }

  const formContent =
    profileReady && fullProfileFormValues ? (
      <ProfileMultiForm
        handleProfileUpdate={handleProfileUpdate}
        profile={fullProfileFormValues}
        ready={ready}
      />
    ) : (
      <Spinner className="mt-3" />
    );

  return (
    <Layout title={t("pages.profile")}>
      {profile && <MemberDetails userProfile={profile} />}
      <Text as="p" variant="body">
        {t("profileInfo.profile")}
      </Text>
      <div>{formContent}</div>
    </Layout>
  );
};

export default ProfilePage;
